import api from './api'

export interface Tenant {
  tenant_id: string
  company_name: string
  contact_name: string
  contact_phone: string
  contact_email: string
  created_at: string
  updated_at: string
  status?: 'active' | 'suspended' | 'trial'
  subscription_plan?: string
  payment_provider?: 'stripe' | 'nmi'
  subdomain?: string
  user_count?: number
  trial_ends_at?: string
  /** SUB-3.9. Null or absent means a top-level account. */
  parent_tenant_id?: string | null
  /**
   * SUB-3.9. An archived child still blocks disabling sub-accounts (rule 4 of
   * `enforce_one_level_hierarchy`) while being excluded from the allowance count
   * (FR67), so the sub-accounts list has to be able to tell the two apart.
   */
  is_archived?: boolean | null
  /**
   * SUB-3.10. A structural capability, NOT a count. The transfer destination picker
   * needs to know which accounts can hold client accounts at all.
   *
   * ⚠️ The allowance is deliberately absent from this DTO. Whether a destination has
   * room is `sub_account_usage()`, which applies FR67, and a count in the browser
   * would disagree with it the first time a sub-account was archived.
   */
  sub_accounts_enabled?: boolean | null
  /**
   * ADM-1.3. `tenant_settings.commission_costing_enabled`, filled by the list only.
   * ⚠️ Null is UNKNOWN (no settings row, or the read failed), never off.
   */
  commission_costing_enabled?: boolean | null
}

export interface CreateTenantDto extends Record<string, unknown> {
  company_name: string
  contact_name: string
  contact_phone: string
  contact_email: string
}

export interface UpdateTenantDto extends Partial<CreateTenantDto> {
  status?: 'active' | 'suspended' | 'trial'
  payment_provider?: 'stripe' | 'nmi'
}

/** SUB-2.2. Mirrors `public.sub_account_usage(uuid)`. `allowance` null = unlimited. */
export interface SubAccountUsageDto {
  tenant_id: string
  enabled: boolean
  used: number
  allowance: number | null
  unlimited: boolean
  at_limit: boolean
}

export interface SetSubAccountsDto {
  enabled: boolean
  /** Always sent, including as null. See `setSubAccounts`. */
  allowance: number | null
}

/** SUB-3.9. Mirrors `public.detach_sub_account(uuid)` after 20260812n. */
export interface DetachSubAccountDto {
  status: string
  child_tenant_id: string
  former_parent_tenant_id: string | null
  /** True when at least one parent-derived grant was removed. */
  access_revoked: boolean
  /** The count the database measured. Never computed in the browser. */
  grants_revoked: number
}

/** SUB-3.10. Mirrors `public.transfer_sub_account(uuid, uuid)` from 20260813h. */
export interface TransferSubAccountDto {
  status: string
  child_tenant_id: string
  former_parent_tenant_id: string | null
  new_parent_tenant_id: string | null
  /** True when at least one grant of the outgoing provider was removed. */
  access_revoked: boolean
  /** The count the database measured, either side of its own UPDATE. */
  grants_revoked: number
  /** True when at least one grant for the incoming provider was created. */
  access_granted: boolean
  /** The count the database measured. Never computed in the browser. */
  grants_granted: number
  /**
   * Stated by the server so that `grants_granted: 0` on an archived client account
   * is not read as a failure: an archived sub-account holds no live parent-derived
   * grants until it is restored.
   */
  child_is_archived: boolean
}

class TenantsService {
  async list(): Promise<Tenant[]> {
    const response = await api.get('/tenants')
    return response.data
  }

  async get(tenantId: string): Promise<Tenant> {
    const response = await api.get(`/tenants/${tenantId}`)
    return response.data
  }

  async create(data: CreateTenantDto): Promise<Tenant> {
    const response = await api.post('/tenants', data)
    return response.data
  }

  async update(tenantId: string, data: UpdateTenantDto): Promise<Tenant> {
    const response = await api.put(`/tenants/${tenantId}`, data)
    return response.data
  }

  async delete(tenantId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}`)
  }

  async suspend(tenantId: string): Promise<Tenant> {
    const response = await api.post(`/tenants/${tenantId}/suspend`)
    return response.data
  }

  async activate(tenantId: string): Promise<Tenant> {
    const response = await api.post(`/tenants/${tenantId}/activate`)
    return response.data
  }

  async createImpersonationToken(tenantId: string): Promise<{ token: string }> {
    const response = await api.post(`/tenants/${tenantId}/impersonate`)
    return response.data
  }

  /**
   * SUB-2.2 · FR5. Current sub-account usage and allowance for one tenant.
   *
   * The count is not computed anywhere in this app. `sub_account_usage()` in the
   * database applies FR67 (an archived sub-account does not consume the
   * allowance); a second count here would disagree with it the first time a
   * sub-account was archived.
   */
  async getSubAccounts(tenantId: string): Promise<SubAccountUsageDto> {
    const response = await api.get(`/tenants/${tenantId}/sub-accounts`)
    return response.data
  }

  /**
   * ADM-1.3. Turn commission costing on or off for one account.
   *
   * The backend calls `set_tenant_commission_costing_enabled()` on the caller's
   * own token (ADM-1.2), so the database, not this app, decides who may. Returns
   * the flag the database ended with, not the request.
   */
  async setCommissionCosting(tenantId: string, enabled: boolean): Promise<boolean> {
    const response = await api.put(`/tenants/${tenantId}/commission-costing`, { enabled })
    return response.data.enabled
  }

  /**
   * SUB-2.2 · FR1, FR2, FR3, FR68. Enable or disable sub-accounts and set the cap.
   *
   * `allowance` is always sent, including as null, because the underlying RPC's
   * p_allowance defaults to NULL: an omitted value would silently mean unlimited,
   * and FR3 asks for a deliberate choice between a cap and no cap.
   *
   * Returns the row as it ended up, not the request. On disable the database also
   * clears the allowance, so the arguments are not the resulting state.
   */
  async setSubAccounts(
    tenantId: string,
    input: SetSubAccountsDto,
  ): Promise<SubAccountUsageDto> {
    const response = await api.put(`/tenants/${tenantId}/sub-accounts`, {
      enabled: input.enabled,
      allowance: input.allowance,
    })
    return response.data
  }

  /**
   * SUB-3.9 · FR53, FR55, FR56. Release a sub-account into a standalone account.
   *
   * `childTenantId` is the CHILD. There is no parent argument, and that is FR55
   * rather than an omission: the RPC reads nothing about the parent, so an
   * archived, defunct or uncooperative provider cannot hold a client's data.
   *
   * The revocation is not requested separately and must not be. `parent_tenant_id
   * := NULL` fires `trg_sync_agency_grants_from_tenants`, which reconciles the
   * former parent inside that same statement. `grants_revoked` in the answer is
   * the count that reconciliation actually removed.
   */
  async detachSubAccount(childTenantId: string): Promise<DetachSubAccountDto> {
    const response = await api.post(`/tenants/${childTenantId}/detach`)
    return response.data
  }

  /**
   * SUB-3.10 · FR54, FR69, FR56. Move a client account to a different provider.
   *
   * `childTenantId` is the CHILD. There is no outgoing-provider argument: the server
   * derives it from the account's own `parent_tenant_id`, because accepting it would
   * let a caller name a provider the account is not under, and a caller-supplied
   * filter is not a filter (finding F-1).
   *
   * ⚠️ ONE CALL, AND NEVER detach THEN attach. FR54 requires the outgoing provider's
   * access to be revoked AS the incoming provider's is granted. Two calls are two
   * transactions, and between them the account has no provider and nobody has agency
   * access to it. `transfer_sub_account` sets the column once and the reconciling
   * trigger handles both halves inside that single statement.
   */
  async transferSubAccount(
    childTenantId: string,
    newParentTenantId: string,
  ): Promise<TransferSubAccountDto> {
    const response = await api.post(`/tenants/${childTenantId}/transfer`, {
      new_parent_tenant_id: newParentTenantId,
    })
    return response.data
  }
}

export const tenantsService = new TenantsService()